/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {forwardRef, useEffect, useRef, useState} from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {ptBR} from 'date-fns/locale/pt-BR'
import {getConcessionariesAll} from '../../../../../../services/concessionariesService'
import {getUnitConsumerAll} from '../../../../../../services/unitConsumerService'
import {Loading} from '../../../../../components/Loadings/loading'
import MyPDFViewer from '../../../../../components/pdfReader'
import {putInvoiceExtract} from '../../../../../../services/invoices'
import {parseDate} from '../../../../../../utils/date'
import {useToast} from '../../../../../components/Toats'
import {LoadingSpan} from '../../../../../components/Loadings/loadingSpan'
import {KTSVG} from '../../../../../../../_metronic/helpers'
import {
  FieldError,
  FieldLabel,
  FormSection,
} from '../../../../../unit-consumer/components/ModalAddEditUnitConsumer/components/FormSection'
import {getConcessionaireImageSrc} from '../../../../../unit-consumer/components/TableUnitConsumers/BoardUnitConsumers'
import ucStyles from '../../../../../unit-consumer/components/ModalAddEditUnitConsumer/modalUnitConsumer.module.css'
import styles from '../../modalInvoice.module.css'

const isGrupoATarifario = (grupo: string | null | undefined) => {
  const g = (grupo ?? '').trim().toUpperCase()
  return g.startsWith('A')
}

const jsonFieldToForm = (v: any, fallback: string | null): string | null => {
  if (v === undefined || v === null) return fallback
  if (typeof v === 'string') return v
  try {
    return JSON.stringify(v)
  } catch {
    return fallback
  }
}

const mergeInvoiceExtractIntoFormValues = (ext: any, cur: any) => {
  const s = (k: string): string | null => {
    if (!(k in ext) || ext[k] === undefined || ext[k] === null) return cur[k]
    return typeof ext[k] === 'string' ? ext[k] : String(ext[k])
  }
  const dateish = (k: string): any => {
    if (!(k in ext) || ext[k] === undefined || ext[k] === null) return cur[k]
    return ext[k]
  }
  const mergedGrupoTarifario = s('grupo_tarifario')
  return {
    ...cur,
    data_vencimento: dateish('data_vencimento'),
    energia_faturada: s('energia_faturada'),
    energia_injetada: s('energia_injetada'),
    energia_registrada: s('energia_registrada'),
    energia_compensada: s('energia_compensada'),
    leitura_atual: dateish('leitura_atual'),
    leitura_anterior: dateish('leitura_anterior'),
    leitura_proxima: dateish('leitura_proxima'),
    tarifa: s('tarifa'),
    tipo_fornecimento: s('tipo_fornecimento'),
    valor_faturado: s('valor_faturado'),
    valor_economizado: s('valor_economizado'),
    valor_consumido: s('valor_consumido'),
    link_file: s('link_file'),
    base64:
      ext.base64 !== undefined && ext.base64 !== null
        ? typeof ext.base64 === 'string'
          ? ext.base64
          : String(ext.base64)
        : cur.base64,
    creditos: s('creditos'),
    taxa_iluminacao: s('taxa_iluminacao'),
    icms: s('icms'),
    pis: s('pis'),
    cofins: s('cofins'),
    status: ext.status != null && ext.status !== '' ? ext.status : cur.status,
    idConcessionarie: cur.idConcessionarie,
    idUnitConsumer: cur.idUnitConsumer,
    mes: cur.mes,
    confirm_payment: cur.confirm_payment,
    codigo_instalacao: s('codigo_instalacao'),
    grupo_tarifario: mergedGrupoTarifario,
    tipo_fatura_ab: isGrupoATarifario(mergedGrupoTarifario) ? 'A' : 'B',
    modalidade_tarifaria: s('modalidade_tarifaria'),
    concessionaria: s('concessionaria'),
    regulamentacao_aneel:
      typeof ext.regulamentacao_aneel === 'boolean'
        ? ext.regulamentacao_aneel
        : cur.regulamentacao_aneel,
    demanda_contratada_kw: s('demanda_contratada_kw'),
    demanda_medida_kw: s('demanda_medida_kw'),
    demanda_faturada_kw: s('demanda_faturada_kw'),
    ultrapassagem_kw: s('ultrapassagem_kw'),
    fator_potencia: s('fator_potencia'),
    consumo_ponta_kwh: s('consumo_ponta_kwh'),
    consumo_fora_ponta_kwh: s('consumo_fora_ponta_kwh'),
    bandeira_tarifaria_vigente: s('bandeira_tarifaria_vigente'),
    tarifa_tusd: s('tarifa_tusd'),
    tarifa_te: s('tarifa_te'),
    tarifa_media: s('tarifa_media'),
    tarifa_tusd_ponta: s('tarifa_tusd_ponta'),
    tarifa_tusd_fora_ponta: s('tarifa_tusd_fora_ponta'),
    tarifa_te_ponta: s('tarifa_te_ponta'),
    tarifa_te_fora_ponta: s('tarifa_te_fora_ponta'),
    tarifa_demanda_contratada: s('tarifa_demanda_contratada'),
    preco_unitario_ponta:
      ext.preco_unitario_ponta !== undefined && ext.preco_unitario_ponta !== null
        ? String(ext.preco_unitario_ponta)
        : cur.preco_unitario_ponta,
    preco_unitario_fora_ponta:
      ext.preco_unitario_fora_ponta !== undefined && ext.preco_unitario_fora_ponta !== null
        ? String(ext.preco_unitario_fora_ponta)
        : cur.preco_unitario_fora_ponta,
    contrib_ilum_publica: s('contrib_ilum_publica'),
    itens_faturamento_json: jsonFieldToForm(ext.itens_faturamento_json, cur.itens_faturamento_json),
    medicao_json: jsonFieldToForm(ext.medicao_json, cur.medicao_json),
    historico_consumo_json: jsonFieldToForm(ext.historico_consumo_json, cur.historico_consumo_json),
    tarifas_json: jsonFieldToForm(ext.tarifas_json, cur.tarifas_json),
    demandas_grupo_a_json: jsonFieldToForm(ext.demandas_grupo_a_json, cur.demandas_grupo_a_json),
    impostos_json: jsonFieldToForm(ext.impostos_json, cur.impostos_json),
  }
}

type DateFieldProps = {
  id: string
  value: Date | null
  onChange?: (date: Date) => void
  monthYear?: boolean
  disabled?: boolean
}

const DateFieldInput = forwardRef<HTMLInputElement, any>(({value, onClick, disabled, id}, ref) => (
  <div
    className={`input-group input-group-solid ${styles.dateGroup} ${
      disabled ? styles.dateGroupDisabled : ''
    }`}
    onClick={disabled ? undefined : onClick}
  >
    <span className='input-group-text'>
      <i className='bi bi-calendar3 fs-5 text-reset'></i>
    </span>
    <input
      id={id}
      ref={ref}
      type='text'
      className='form-control form-control-solid'
      value={value}
      placeholder='Selecione'
      disabled={disabled}
      readOnly
    />
  </div>
))

const DateField = ({id, value, onChange, monthYear, disabled}: DateFieldProps) => (
  <div className={styles.dateField}>
    <DatePicker
      selected={value}
      onChange={(date) => date && onChange?.(date as Date)}
      dateFormat={monthYear ? 'MM/yyyy' : 'dd/MM/yyyy'}
      showMonthYearPicker={monthYear}
      locale={ptBR}
      disabled={disabled}
      customInput={<DateFieldInput id={id} disabled={disabled} />}
    />
  </div>
)

type UnitFieldProps = {
  id: string
  name: string
  formik: any
  unit?: string
  prefix?: string
  placeholder?: string
  invalid?: boolean
}

const UnitField = ({id, name, formik, unit, prefix, placeholder, invalid}: UnitFieldProps) => (
  <div
    className={`input-group input-group-solid ${styles.unitGroup} ${
      invalid ? styles.unitGroupInvalid : ''
    }`}
  >
    {prefix && <span className='input-group-text'>{prefix}</span>}
    <input
      id={id}
      type='text'
      inputMode='decimal'
      className='form-control form-control-solid'
      placeholder={placeholder ?? '0,00'}
      value={formik.values[name] ?? ''}
      onChange={formik.handleChange(name)}
    />
    {unit && <span className='input-group-text'>{unit}</span>}
  </div>
)

const toDate = (value: string | null | undefined): Date | null => {
  if (!value) return null
  const date = new Date(parseDate(value))
  return isNaN(date.getTime()) ? null : date
}

const addOneMonth = (date: Date | null) => {
  if (!date) return null
  const next = new Date(date)
  next.setMonth(next.getMonth() + 1)
  return next
}

function GrupoATarifarioFields({formik}: {formik: any}) {
  const fields: {name: string; label: string; hint: string; unit?: string; prefix?: string}[] = [
    {
      name: 'demanda_contratada_kw',
      label: 'Demanda contratada',
      hint: 'Demanda contratada em kW',
      unit: 'kW',
    },
    {
      name: 'tarifa_demanda_contratada',
      label: 'Tarifa demanda contratada',
      hint: 'Tarifa da demanda contratada',
      prefix: 'R$',
    },
    {
      name: 'consumo_ponta_kwh',
      label: 'Consumo ponta',
      hint: 'Consumo no horário de ponta',
      unit: 'kWh',
    },
    {
      name: 'consumo_fora_ponta_kwh',
      label: 'Consumo fora ponta',
      hint: 'Consumo fora do horário de ponta',
      unit: 'kWh',
    },
    {
      name: 'tarifa_te_ponta',
      label: 'Tarifa TE ponta',
      hint: 'Tarifa TE no horário de ponta',
      prefix: 'R$',
    },
    {
      name: 'tarifa_tusd_ponta',
      label: 'Tarifa TUSD ponta',
      hint: 'Tarifa TUSD no horário de ponta',
      prefix: 'R$',
    },
    {
      name: 'tarifa_te_fora_ponta',
      label: 'Tarifa TE fora ponta',
      hint: 'Tarifa TE fora do horário de ponta',
      prefix: 'R$',
    },
    {
      name: 'tarifa_tusd_fora_ponta',
      label: 'Tarifa TUSD fora ponta',
      hint: 'Tarifa TUSD fora do horário de ponta',
      prefix: 'R$',
    },
    {
      name: 'preco_unitario_ponta',
      label: 'Preço unitário ponta',
      hint: 'Preço unitário da energia no horário de ponta',
      prefix: 'R$',
    },
    {
      name: 'preco_unitario_fora_ponta',
      label: 'Preço unitário fora ponta',
      hint: 'Preço unitário da energia fora do horário de ponta',
      prefix: 'R$',
    },
  ]

  return (
    <FormSection
      icon='/media/icons/duotune/electronics/elc002.svg'
      title='Grupo A — Demanda e horário de ponta'
      description='Campos específicos de faturas de alta tensão'
    >
      <div className={ucStyles.fieldsGrid}>
        {fields.map((field) => (
          <div key={field.name}>
            <FieldLabel htmlFor={`inv_${field.name}`} hint={field.hint}>
              {field.label}
            </FieldLabel>
            <UnitField
              id={`inv_${field.name}`}
              name={field.name}
              formik={formik}
              unit={field.unit}
              prefix={field.prefix}
            />
          </div>
        ))}
      </div>
    </FormSection>
  )
}

const StepModal = ({hasError, formik, id}: any) => {
  const [unitConsumers, setUnitConsumers] = useState<any[]>([])
  const [concessionaries, setConcessionaries] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false)
  const [loadingConcessionaries, setLoadingConcessionaries] = useState<boolean>(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const {toggleToast} = useToast()

  const getInvoiceOneExtract = async () => {
    try {
      setLoadingPdf(true)
      if (selectedFile) {
        const formData = new FormData()
        formData.append('files', selectedFile)

        const myInvoiceExtract = await putInvoiceExtract(id ? id : '', formData)

        if (myInvoiceExtract) {
          formik.setValues(mergeInvoiceExtractIntoFormValues(myInvoiceExtract, formik.values))
        }
      }
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao extrair dados do arquivo. ', 'error')
    } finally {
      setLoadingPdf(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) setSelectedFile(file)
  }

  const getUnitConsumers = async () => {
    try {
      setLoading(true)
      const myUnitConsumers = await getUnitConsumerAll()
      setUnitConsumers(myUnitConsumers)
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao buscar a lista de unidades consumidoras. ', 'error')
    } finally {
      setLoading(false)
    }
  }

  const getConcessionaries = async () => {
    try {
      setLoadingConcessionaries(true)
      const myConcessionaries = await getConcessionariesAll()
      setConcessionaries(myConcessionaries)
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao buscar a lista concessionárias. ', 'error')
    } finally {
      setLoadingConcessionaries(false)
    }
  }

  const concessionaire = concessionaries.find((item) => item.id === formik.values.idConcessionarie)
  const unitConsumer = unitConsumers.find((item) => item.id === formik.values.idUnitConsumer)
  const extractDisabled = concessionaire?.status_extract === 'NAO EXTRAI'
  const uploadDisabled = loadingPdf || extractDisabled

  const openFilePicker = () => {
    if (!uploadDisabled) fileInputRef.current?.click()
  }

  const clearFile = () => {
    formik.setFieldValue('link_file', '')
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    const fuction_async = async () => {
      await getUnitConsumers()
      await getConcessionaries()
    }
    fuction_async()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getInvoiceOneExtract()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile])

  if (loading || loadingConcessionaries) {
    return (
      <div
        style={{display: 'flex', justifyContent: 'center', height: '15rem', alignItems: 'center'}}
      >
        <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
      </div>
    )
  }

  const leituraAtual = toDate(formik.values.leitura_atual)
  const isPaid = formik.values.confirm_payment === true
  const showError = (value: any) => hasError && !value

  return (
    <div className={styles.grid}>
      <div className={styles.formColumn}>
        <FormSection
          icon='/media/icons/duotune/files/fil003.svg'
          title='Referência'
          description='Período e vínculo da fatura'
        >
          <div className={ucStyles.fieldsGrid}>
            <div>
              <FieldLabel htmlFor='inv_mes' hint='Mês de referência da fatura'>
                Mês/Ano de referência
              </FieldLabel>
              <DateField id='inv_mes' value={toDate(formik.values.mes)} monthYear disabled />
            </div>

            <div>
              <FieldLabel
                htmlFor='inv_tipo_fatura_ab'
                hint='Grupo A: alta tensão / demanda. Grupo B: baixa tensão. Define quais campos adicionais são exibidos.'
              >
                Grupo tarifário
              </FieldLabel>
              <select
                id='inv_tipo_fatura_ab'
                className='form-select form-select-solid'
                value={formik.values.tipo_fatura_ab === 'A' ? 'A' : 'B'}
                onChange={(e) =>
                  formik.setValues({
                    ...formik.values,
                    tipo_fatura_ab: e.target.value === 'A' ? 'A' : 'B',
                  })
                }
              >
                <option value='B'>B — Baixa tensão (convencional)</option>
                <option value='A'>A — Alta tensão (demanda / ponta)</option>
              </select>
            </div>

            <div className={styles.linkCard}>
              <img
                src={getConcessionaireImageSrc(concessionaire?.link_imagem)}
                alt=''
                className={styles.linkLogo}
              />
              <div className={styles.linkText}>
                <span className={styles.linkLabel}>Concessionária</span>
                <span className={styles.linkName} title={concessionaire?.name}>
                  {concessionaire?.name || 'Não informada'}
                </span>
              </div>
            </div>

            <div className={styles.linkCard}>
              <span className={styles.linkIcon}>
                <KTSVG
                  path='/media/icons/duotune/general/gen001.svg'
                  className='svg-icon-2 svg-icon-primary'
                />
              </span>
              <div className={styles.linkText}>
                <span className={styles.linkLabel}>Unidade consumidora</span>
                <span className={styles.linkName} title={unitConsumer?.name}>
                  {unitConsumer?.name || 'Não informada'}
                </span>
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          icon='/media/icons/duotune/general/gen014.svg'
          title='Período de leitura'
          description='Datas de leitura do medidor'
        >
          <div className={styles.fieldsGrid3}>
            <div>
              <FieldLabel htmlFor='inv_leitura_anterior' required hint='Data da leitura anterior'>
                Leitura anterior
              </FieldLabel>
              <DateField
                id='inv_leitura_anterior'
                value={toDate(formik.values.leitura_anterior)}
                onChange={(date) =>
                  formik.setValues({...formik.values, leitura_anterior: date.toISOString()})
                }
              />
              <FieldError show={showError(formik.values.leitura_anterior)}>
                A leitura anterior é obrigatória.
              </FieldError>
            </div>

            <div>
              <FieldLabel htmlFor='inv_leitura_atual' required hint='Data da leitura atual'>
                Leitura atual
              </FieldLabel>
              <DateField
                id='inv_leitura_atual'
                value={leituraAtual}
                onChange={(date) =>
                  formik.setValues({...formik.values, leitura_atual: date.toISOString()})
                }
              />
              <FieldError show={showError(formik.values.leitura_atual)}>
                A leitura atual é obrigatória.
              </FieldError>
            </div>

            <div>
              <FieldLabel htmlFor='inv_leitura_proxima' hint='Data prevista da próxima leitura'>
                Próxima leitura
              </FieldLabel>
              <DateField
                id='inv_leitura_proxima'
                value={toDate(formik.values.leitura_proxima) ?? addOneMonth(leituraAtual)}
                onChange={(date) =>
                  formik.setValues({...formik.values, leitura_proxima: date.toISOString()})
                }
              />
            </div>
          </div>
        </FormSection>

        {formik.values.tipo_fatura_ab === 'A' && <GrupoATarifarioFields formik={formik} />}

        <FormSection
          icon='/media/icons/duotune/general/gen032.svg'
          title='Energia'
          description='Consumo, injeção e créditos do período'
        >
          <div className={ucStyles.fieldsGrid}>
            <div>
              <FieldLabel
                htmlFor='inv_energia_registrada'
                required
                hint='Energia consumida da rede no período'
              >
                Energia registrada
              </FieldLabel>
              <UnitField
                id='inv_energia_registrada'
                name='energia_registrada'
                formik={formik}
                unit='kWh'
                placeholder='0'
                invalid={showError(formik.values.energia_registrada)}
              />
              <FieldError show={showError(formik.values.energia_registrada)}>
                A energia registrada é obrigatória.
              </FieldError>
            </div>

            <div>
              <FieldLabel htmlFor='inv_energia_injetada' hint='Energia injetada na rede no período'>
                Energia injetada
              </FieldLabel>
              <UnitField
                id='inv_energia_injetada'
                name='energia_injetada'
                formik={formik}
                unit='kWh'
                placeholder='0'
              />
            </div>

            <div>
              <FieldLabel htmlFor='inv_energia_faturada' required hint='Energia cobrada na fatura'>
                Energia faturada
              </FieldLabel>
              <UnitField
                id='inv_energia_faturada'
                name='energia_faturada'
                formik={formik}
                unit='kWh'
                placeholder='0'
                invalid={showError(formik.values.energia_faturada)}
              />
              <FieldError show={showError(formik.values.energia_faturada)}>
                A energia faturada é obrigatória.
              </FieldError>
            </div>

            <div>
              <FieldLabel
                htmlFor='inv_energia_compensada'
                required
                hint='Energia compensada com créditos'
              >
                Energia compensada
              </FieldLabel>
              <UnitField
                id='inv_energia_compensada'
                name='energia_compensada'
                formik={formik}
                unit='kWh'
                placeholder='0'
              />
            </div>

            <div className={ucStyles.fieldFull}>
              <FieldLabel
                htmlFor='inv_creditos'
                required
                hint='Créditos acumulados após esta fatura'
              >
                Crédito acumulado
              </FieldLabel>
              <UnitField
                id='inv_creditos'
                name='creditos'
                formik={formik}
                unit='kWh'
                placeholder='0'
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          icon='/media/icons/duotune/finance/fin010.svg'
          title='Valores'
          description='Valores cobrados, tarifa e tributos'
        >
          <div className={ucStyles.fieldsGrid}>
            <div>
              <FieldLabel htmlFor='inv_valor_faturado' required hint='Valor total da fatura'>
                Valor faturado
              </FieldLabel>
              <UnitField
                id='inv_valor_faturado'
                name='valor_faturado'
                formik={formik}
                prefix='R$'
                invalid={showError(formik.values.valor_faturado)}
              />
              <FieldError show={showError(formik.values.valor_faturado)}>
                O valor faturado é obrigatório.
              </FieldError>
            </div>

            <div>
              <FieldLabel htmlFor='inv_valor_economizado' hint='Economia obtida com a geração'>
                Valor economizado
              </FieldLabel>
              <UnitField
                id='inv_valor_economizado'
                name='valor_economizado'
                formik={formik}
                prefix='R$'
              />
            </div>

            <div>
              <FieldLabel htmlFor='inv_tarifa' required hint='Preço / tarifa média por kWh'>
                Tarifa média
              </FieldLabel>
              <UnitField
                id='inv_tarifa'
                name='tarifa'
                formik={formik}
                prefix='R$'
                unit='/kWh'
                invalid={showError(formik.values.tarifa)}
              />
              <FieldError show={showError(formik.values.tarifa)}>
                A tarifa é obrigatória.
              </FieldError>
            </div>

            <div>
              <FieldLabel htmlFor='inv_taxa_iluminacao' hint='Contribuição de iluminação pública'>
                Taxa de iluminação
              </FieldLabel>
              <UnitField
                id='inv_taxa_iluminacao'
                name='taxa_iluminacao'
                formik={formik}
                prefix='R$'
              />
            </div>
          </div>

          <div className={`${styles.fieldsGrid3} mt-4`}>
            <div>
              <FieldLabel htmlFor='inv_icms'>ICMS</FieldLabel>
              <UnitField id='inv_icms' name='icms' formik={formik} prefix='R$' />
            </div>
            <div>
              <FieldLabel htmlFor='inv_pis'>PIS/PASEP</FieldLabel>
              <UnitField id='inv_pis' name='pis' formik={formik} prefix='R$' />
            </div>
            <div>
              <FieldLabel htmlFor='inv_cofins'>COFINS</FieldLabel>
              <UnitField id='inv_cofins' name='cofins' formik={formik} prefix='R$' />
            </div>
          </div>
        </FormSection>

        <FormSection
          icon='/media/icons/duotune/general/gen048.svg'
          title='Status'
          description='Situação da fatura e do pagamento'
        >
          <div className={styles.statusRow}>
            <div className={styles.statusText}>
              <span className={styles.statusTitle}>Fatura informada</span>
              <span className={styles.statusDescription}>
                Marca a fatura como informada com os valores preenchidos
              </span>
            </div>
            <div className='form-check form-check-solid form-switch'>
              <input
                className='form-check-input w-45px h-25px'
                type='checkbox'
                id='inv_status'
                checked={formik.values.status === 'INFORMADO'}
                onChange={(event) =>
                  formik.setValues({
                    ...formik.values,
                    status: event.target.checked ? 'INFORMADO' : 'NÃO INFORMADO',
                  })
                }
              />
            </div>
          </div>

          <div className={styles.statusRow}>
            <div className={styles.statusText}>
              <span className={styles.statusTitle}>Pagamento</span>
              <span className={styles.statusDescription}>
                Confirme se o cliente já pagou a fatura
              </span>
            </div>
            <div className={styles.segmented} role='radiogroup' aria-label='Pagamento'>
              <button
                type='button'
                role='radio'
                aria-checked={!isPaid}
                className={`${styles.segment} ${!isPaid ? styles.segmentPending : ''}`}
                onClick={() => formik.setValues({...formik.values, confirm_payment: false})}
              >
                <i className='bi bi-clock text-reset'></i>
                Pendente
              </button>
              <button
                type='button'
                role='radio'
                aria-checked={isPaid}
                className={`${styles.segment} ${isPaid ? styles.segmentPaid : ''}`}
                onClick={() => formik.setValues({...formik.values, confirm_payment: true})}
              >
                <i className='bi bi-cash-coin text-reset'></i>
                Pago
              </button>
            </div>
          </div>
        </FormSection>
      </div>

      <section className={`${ucStyles.section} ${styles.pdfCard}`}>
        <div className={styles.pdfToolbar}>
          <span className={ucStyles.sectionIcon}>
            <KTSVG
              path='/media/icons/duotune/files/fil021.svg'
              className='svg-icon-2 svg-icon-primary'
            />
          </span>
          <div className={styles.pdfToolbarText}>
            <h4 className={ucStyles.sectionTitle}>Arquivo da fatura</h4>
            <span className={ucStyles.sectionDescription}>
              {selectedFile ? (
                <span className={styles.pdfFileName}>{selectedFile.name}</span>
              ) : extractDisabled ? (
                'Esta concessionária não permite extração automática'
              ) : (
                'Envie o PDF para preencher os campos automaticamente'
              )}
            </span>
          </div>
          <div className={styles.pdfToolbarActions}>
            {formik.values.link_file && !loadingPdf && (
              <button type='button' className='btn btn-sm btn-light' onClick={clearFile}>
                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-4 me-1' />
                Remover
              </button>
            )}
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              disabled={uploadDisabled}
              onClick={openFilePicker}
            >
              <KTSVG path='/media/icons/duotune/files/fil022.svg' className='svg-icon-4 me-1' />
              {formik.values.link_file ? 'Trocar PDF' : 'Enviar PDF'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type='file'
            accept='application/pdf'
            className='d-none'
            disabled={uploadDisabled}
            onChange={handleFileChange}
          />
        </div>

        {loadingPdf && (
          <div className={styles.processing}>
            <LoadingSpan />
            Processando fatura e extraindo os dados...
          </div>
        )}

        <div className={styles.pdfBody}>
          {formik.values.link_file ? (
            <MyPDFViewer link_file={formik.values.link_file} />
          ) : (
            <div
              className={`${styles.pdfEmpty} ${uploadDisabled ? styles.pdfEmptyDisabled : ''}`}
              onClick={openFilePicker}
            >
              <KTSVG
                path='/media/icons/duotune/files/fil022.svg'
                className='svg-icon-3x svg-icon-muted'
              />
              <span className='fw-bold fs-6 text-gray-700'>Nenhum PDF anexado</span>
              <span>
                {extractDisabled
                  ? 'O upload está desabilitado para esta concessionária'
                  : 'Clique para selecionar o arquivo da fatura (PDF)'}
              </span>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export {StepModal}
